import type { FunctionComponentWithRef } from "react";
import React, { useCallback, useEffect, useRef } from "react";
import { graphql, useFragment } from "react-relay";
import type { Swiper } from "swiper";
import { Navigation } from "swiper/modules";
import type { SwiperRef } from "swiper/react";

import {
  newsletterCategory as newsletterCategoryData,
  newsletter as newsletterData,
} from "shared/data/newsletter";
import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";

import type { newsletterCategoryItemSlideshowNewsletterCategory$key } from "scmp-app/queries/__generated__/newsletterCategoryItemSlideshowNewsletterCategory.graphql";

import { getSize } from "./helpers";
import { MyNewsNewsletterItem } from "./mynews-newsletter-item";
import { NewsletterItem } from "./newsletter-item";
import { PlusNewsletterItem } from "./plus-newsletter-item";
import {
  ArrowContainer,
  ArrowNext,
  ArrowPrevious,
  Container,
  Header,
  List,
  StyledSwiper,
  StyledSwiperSlide,
} from "./styles";

export type Props = React.DetailsHTMLAttributes<HTMLDivElement> & {
  reference: newsletterCategoryItemSlideshowNewsletterCategory$key;
};

export const NewsletterCategorySlideshow: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  ref: forwardReference,
  reference,
  ...props
}) => {
  const newsletterCategory = useFragment(
    graphql`
      fragment newsletterCategoryItemSlideshowNewsletterCategory on NewsletterCategory {
        entityId
        name
        newsletters(first: 100, orderBy: { field: WEIGHT, direction: DESC }) {
          edges {
            node {
              entityId
              name
              ...helpersNewsletterSlideshowItemGetSizeNewsletter
              ...mynewsNewsletterItemNewsletter
              ...newsletterItemNewsletter
              ...plusNewsletterItemNewsletter
            }
          }
        }
      }
    `,
    reference,
  );

  const swiperReference = useRef<SwiperRef>(null);
  const isTabletUp = useResponsive(theme.breakpoints.up("tablet"), false);
  const [isPreviousDisable, setIsPreviousDisable] = React.useState(true);
  const [isNextDisable, setIsNextDisable] = React.useState(false);
  const [isLocked, setIsLocked] = React.useState(false);

  const handleArrowClick = useCallback((direction: "next" | "prev") => {
    if (!swiperReference.current) return;

    switch (direction) {
      case "next":
        swiperReference.current.swiper.slideNext();
        break;
      case "prev":
        swiperReference.current.swiper.slidePrev();
        break;
    }
  }, []);

  useEffect(() => {
    if (!swiperReference.current) return;
    const instance = swiperReference.current.swiper;

    const updateButtonStates = () => {
      setIsPreviousDisable(instance.isBeginning);
      setIsNextDisable(instance.isEnd);
    };
    updateButtonStates();

    const handleLockState = (swiper: Swiper) => {
      setIsLocked(swiper.isLocked);
    };

    instance.on("slideChange", updateButtonStates);
    instance.on("reachBeginning", updateButtonStates);
    instance.on("reachEnd", updateButtonStates);
    instance.on("init", updateButtonStates);
    instance.on("resize", updateButtonStates);
    instance.on("lock", handleLockState);
    instance.on("unlock", handleLockState);

    return () => {
      instance.off("slideChange", updateButtonStates);
      instance.off("reachBeginning", updateButtonStates);
      instance.off("reachEnd", updateButtonStates);
      instance.off("init", updateButtonStates);
      instance.off("resize", updateButtonStates);
      instance.off("lock", handleLockState);
      instance.off("unlock", handleLockState);
    };
  }, []);

  return (
    <Container ref={forwardReference} {...props}>
      <Header>
        {newsletterCategory.name} ({newsletterCategory.newsletters?.edges.length})
        <ArrowContainer>
          <ArrowPrevious
            $isDisabled={isPreviousDisable || isLocked}
            onClick={() => handleArrowClick("prev")}
          />
          <ArrowNext
            $isDisabled={isNextDisable || isLocked}
            onClick={() => handleArrowClick("next")}
          />
        </ArrowContainer>
      </Header>

      <List>
        <StyledSwiper
          modules={[Navigation]}
          pagination={false}
          ref={swiperReference}
          slidesOffsetAfter={isTabletUp ? 0 : 20}
          slidesOffsetBefore={isTabletUp ? 0 : 20}
          slidesPerGroupAuto
          slidesPerView="auto"
          spaceBetween={20}
        >
          {newsletterCategory.newsletters?.edges.map(
            ({ node }) =>
              node && (
                <StyledSwiperSlide $size={getSize(node)} key={node.entityId}>
                  {(() => {
                    switch (newsletterCategory.entityId) {
                      case newsletterCategoryData.subscriberOnly.entityId:
                        return <PlusNewsletterItem reference={node} />;
                      default:
                        if (node.entityId === newsletterData.myNews.entityId) {
                          return <MyNewsNewsletterItem reference={node} />;
                        }
                        return <NewsletterItem reference={node} />;
                    }
                  })()}
                </StyledSwiperSlide>
              ),
          )}
        </StyledSwiper>
      </List>
    </Container>
  );
};

NewsletterCategorySlideshow.displayName = "NewsletterCategorySlideshow";
